import { dsEnvelopeService } from '@services/dsEnvelopeService';
import type { EnvelopeDocument } from '@workpay/ds-shared/types/envelope';

/**
 * Download every signed document in an envelope. Blobs fetch in parallel;
 * click() fires sequentially so Chrome's "allow multiple downloads" prompt
 * treats them as one user-initiated batch instead of blocking all but the
 * first. Throws on fetch failure — caller is responsible for toasting.
 */
export async function downloadAllSignedDocuments(
  envelopeId: string,
  documents: readonly EnvelopeDocument[] | undefined,
): Promise<void> {
  const targets = (documents ?? []).filter(
    (d): d is EnvelopeDocument & { id: string } => typeof d.id === 'string',
  );
  if (targets.length === 0) return;

  const fetched = await Promise.all(
    targets.map(async (doc) => {
      const response = await dsEnvelopeService.downloadSignedDocument(envelopeId, doc.id);
      return { doc, blob: response.data };
    }),
  );

  for (const { doc, blob } of fetched) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.signedFileId ? `signed-${doc.name}` : doc.name || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
